import { Injectable, inject } from '@angular/core';

import { LoggerProvider } from './logger';
import { isSafeUrl } from '../utils/safe-url.util';

/**
 * Browser navigation utility.
 * Encapsulates browser-level navigation operations (window.location).
 * Use this instead of accessing window.location directly for better testability.
 *
 * Note: These methods trigger full page reloads and bypass Angular router.
 * For in-app navigation, use Angular Router instead.
 */
@Injectable({ providedIn: 'root' })
export class BrowserNavigation {
  private readonly _logger = inject(LoggerProvider);

  /**
   * Navigates to the specified URL using window.location.
   * Triggers a full page reload (bypasses Angular router).
   *
   * Only relative paths and absolute `http(s)` URLs are allowed. Any other URL
   * (`javascript:`, `data:`, protocol-relative, ...) is rejected and ignored to
   * prevent XSS through attacker-controlled redirects (HCMI-831).
   *
   * @param url - Relative path or absolute http(s) URL to navigate to
   *
   * @example
   * ```typescript
   * browserNavigation.navigateToUrl('/login');
   * browserNavigation.navigateToUrl('https://example.com');
   * browserNavigation.navigateToUrl('javascript:alert(1)'); // ignored
   * ```
   */
  public navigateToUrl(url: string): void {
    if (!isSafeUrl(url)) {
      this._logger.error('BrowserNavigation: blocked navigation to unsafe URL', url);
      return;
    }

    window.location.href = url;
  }

  /**
   * Reloads the current page.
   *
   * @example
   * ```typescript
   * browserNavigation.reload();
   * ```
   */
  public reload(): void {
    window.location.reload();
  }

  /**
   * Gets the current browser URL.
   *
   * @returns The current URL as a string
   *
   * @example
   * ```typescript
   * const currentUrl = browserNavigation.getCurrentUrl();
   * ```
   */
  public getCurrentUrl(): string {
    return window.location.href;
  }

  /**
   * Gets the current pathname (without domain).
   *
   * @returns The current pathname
   *
   * @example
   * ```typescript
   * // On https://example.com/dashboard
   * const path = browserNavigation.getPathname(); // '/dashboard'
   * ```
   */
  public getPathname(): string {
    return window.location.pathname;
  }
}
